import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";

type InstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

type InstallState = {
  installed: boolean;
  available: boolean;
  installing: boolean;
  install: () => Promise<"accepted" | "dismissed" | "instructions" | "error">;
};

const InstallContext = createContext<InstallState | null>(null);

/** The browser owns installation. Keep its one-use prompt across route changes. */
export function InstallProvider({ children }: { children: ReactNode }) {
  const prompt = useRef<InstallPrompt | null>(null);
  const busy = useRef(false);
  const [available, setAvailable] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(() => standalone());

  useEffect(() => {
    const display = window.matchMedia?.("(display-mode: standalone)");
    function ready(event: Event) {
      event.preventDefault();
      prompt.current = event as InstallPrompt;
      setAvailable(true);
    }
    function completed() {
      prompt.current = null;
      setAvailable(false);
      setInstalled(true);
    }
    function displayChanged() {
      setInstalled(standalone());
    }
    window.addEventListener("beforeinstallprompt", ready);
    window.addEventListener("appinstalled", completed);
    display?.addEventListener("change", displayChanged);
    return () => {
      window.removeEventListener("beforeinstallprompt", ready);
      window.removeEventListener("appinstalled", completed);
      display?.removeEventListener("change", displayChanged);
    };
  }, []);

  async function install(): Promise<"accepted" | "dismissed" | "instructions" | "error"> {
    if (busy.current) return "dismissed";
    const pending = prompt.current;
    if (!pending) return "instructions";
    busy.current = true;
    setInstalling(true);
    prompt.current = null;
    setAvailable(false);
    try {
      // Call before the first await: the native dialog requires a user gesture.
      await pending.prompt();
      const { outcome } = await pending.userChoice;
      // Only appinstalled confirms installation; accepting the dialog is not enough.
      return outcome;
    } catch {
      return "error";
    } finally {
      busy.current = false;
      setInstalling(false);
    }
  }

  return (
    <InstallContext value={{ installed, available, installing, install }}>
      {children}
    </InstallContext>
  );
}

export function useInstall(): InstallState {
  const value = useContext(InstallContext);
  if (!value) throw new Error("InstallProvider is missing");
  return value;
}

function standalone(): boolean {
  return Boolean(
    window.matchMedia?.("(display-mode: standalone)").matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone,
  );
}
